import { Link } from "react-router-dom";
import { Users, Eye, MessageCircle, AlertTriangle, Heart, Phone, BookOpen, CheckCircle, HelpCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const WARNING_SIGNS = [
  { sign: "Zieht sich zurück, will nicht mehr zur Schule", emoji: "😔" },
  { sign: "Schläft schlecht oder hat Albträume", emoji: "😴" },
  { sign: "Verliert Appetit oder isst plötzlich viel mehr", emoji: "🍽️" },
  { sign: "Noten werden schlechter", emoji: "📉" },
  { sign: "Will nicht mehr über den Tag erzählen", emoji: "🤐" },
  { sign: "Hat plötzlich keine Freunde mehr", emoji: "👤" },
  { sign: "Sachen gehen kaputt oder verschwinden", emoji: "📱" },
  { sign: "Weint oft oder ist sehr gereizt", emoji: "😢" },
];

const DO_TIPS = [
  { tip: "Zuhören ohne zu urteilen", desc: "Lassen Sie Ihr Kind ausreden. Unterbrechen Sie nicht mit 'Warum hast du nicht...'" },
  { tip: "Ernst nehmen", desc: "Mobbing ist kein normaler Teil des Aufwachsens. Bagatellisieren Sie es nicht." },
  { tip: "Nicht überreagieren", desc: "Bleiben Sie ruhig. Ihr Kind braucht jetzt Sicherheit, keine Panik." },
  { tip: "Gemeinsam Lösungen suchen", desc: "Fragen Sie: 'Was würde dir helfen?' statt alles selbst zu entscheiden." },
  { tip: "Dokumentieren", desc: "Helfen Sie beim Sichern von Beweisen (Screenshots, Nachrichten)." },
  { tip: "Professionelle Hilfe holen", desc: "Scheuen Sie sich nicht, Beratungsstellen oder die Schule einzubeziehen." },
];

const DONT_TIPS = [
  "Sagen Sie nicht: 'Wehr dich doch!' - Das kann alles schlimmer machen",
  "Konfrontieren Sie die Mobber nicht direkt ohne Absprache",
  "Verbieten Sie nicht einfach das Handy - das bestraft Ihr Kind",
  "Ignorieren Sie die Situation nicht in der Hoffnung, dass es aufhört",
];

export default function Eltern() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO title="Mobbing erkennen & helfen – Ratgeber fuer Eltern" description="Ihr Kind wird gemobbt? Erkenne die Warnzeichen und lerne, wie du helfen kannst. Ratgeber fuer Eltern mit praktischen Tipps und Ressourcen." path="/eltern" />
      
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#FFC4D6]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-[#591C2E]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Informationen für Eltern
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Ihr Kind wird gemobbt? Hier finden Sie Informationen, wie Sie Mobbing erkennen 
              und wie Sie Ihrem Kind helfen können.
            </p>
          </div>

          {/* Warning Signs */}
          <div className="bg-[#FFC4D6]/10 rounded-2xl p-6 md:p-8 mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="w-6 h-6 text-[#591C2E]" />
              <h2 className="text-xl font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>
                Warnzeichen erkennen
              </h2>
            </div>
            <p className="text-[#718096] mb-6">
              Diese Anzeichen können auf Mobbing hindeuten. Nicht jedes Anzeichen bedeutet automatisch Mobbing, 
              aber mehrere zusammen sollten Sie aufmerksam machen:
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {WARNING_SIGNS.map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white rounded-xl p-4">
                  <span className="text-2xl">{item.emoji}</span>
                  <span className="text-[#718096]">{item.sign}</span>
                </div>
              ))}
            </div>
          </div>

          {/* What to do */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
            ✅ So können Sie helfen
          </h2>
          <div className="space-y-4 mb-10">
            {DO_TIPS.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-5 shadow-soft border border-gray-100">
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-[#A0E8AF] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-[#2D3748] mb-1">{item.tip}</h3>
                    <p className="text-[#718096] text-sm">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* What NOT to do */}
          <div className="bg-[#FC8181]/10 border border-[#FC8181]/30 rounded-2xl p-6 md:p-8 mb-10">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#E53E3E]" />
              <h2 className="text-xl font-bold text-[#E53E3E]" style={{ fontFamily: 'Nunito' }}>
                Das sollten Sie vermeiden
              </h2>
            </div>
            <ul className="space-y-3">
              {DONT_TIPS.map((tip, index) => (
                <li key={index} className="flex items-start gap-3 text-[#718096]">
                  <span className="text-[#FC8181]">✗</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
            📚 Weitere Ressourcen
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            <a 
              href="https://www.nummergegenkummer.de/elterntelefon"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-5 shadow-soft border border-gray-100 hover:shadow-hover transition-all"
            >
              <Phone className="w-8 h-8 text-[#A0E8AF] mb-3" />
              <h3 className="font-bold text-[#2D3748] mb-1">Elterntelefon</h3>
              <p className="text-[#718096] text-sm mb-2">0800 111 0 550</p>
              <p className="text-xs text-[#A0AEC0]">Kostenlos & anonym</p>
            </a>
            
            <a 
              href="https://www.klicksafe.de/eltern"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-5 shadow-soft border border-gray-100 hover:shadow-hover transition-all"
            >
              <BookOpen className="w-8 h-8 text-[#BDE0FE] mb-3" />
              <h3 className="font-bold text-[#2D3748] mb-1">klicksafe für Eltern</h3>
              <p className="text-[#718096] text-sm mb-2">Ratgeber zur Medienerziehung</p>
              <p className="text-xs text-[#A0AEC0]">klicksafe.de/eltern</p>
            </a>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#FFF5F7] rounded-2xl p-8 text-center">
            <Heart className="w-10 h-10 text-[#FFC4D6] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Gemeinsam für Ihr Kind
            </h3>
            <p className="text-[#718096] mb-6">
              Sie können hier auch selbst eine Meldung machen, wenn Ihr Kind es nicht alleine möchte.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/melden"
                className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-6 py-3 rounded-full hover:bg-[#86D696] transition-all"
              >
                Vorfall melden
              </Link>
              <Link 
                to="/faq"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#BDE0FE] text-[#1A365D] font-bold px-6 py-3 rounded-full hover:bg-[#BDE0FE] transition-all"
              >
                <HelpCircle className="w-4 h-4" />
                Häufige Fragen
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
